import Vue from 'vue';
import Vuex from 'vuex';
import qs from 'qs';
import axios from 'axios';
import assert from 'assert';
import { get } from 'lodash-es';
import { captureProductionException } from '@/helpers/errorLoggers';
import PaymentFormStore from './PaymentFormStore';
import { postMessage } from '../postMessage';

Vue.use(Vuex);

function getQueryOrderId(query) {
  const [, , explicitOrderId] = window.location.pathname.split('/').filter(item => item);
  if (explicitOrderId) {
    return explicitOrderId;
  }
  return query.order_id;
}

function getParameterByName(name, url) {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

function getQueryOrderToken(query) {
  const explicitOrderToken = getParameterByName('orderToken');
  if (explicitOrderToken) {
    return explicitOrderToken;
  }
  return undefined;
}

export default new Vuex.Store({
  state: {
    apiUrl: '',
    initialLocale: '',
    orderId: '',
    chargeId: '',
    isACH: '',
    query: {},
    printReceiptLoading: true,
    printReceiptData: {},
    options: {},
    lastSize: {
      width: 0,
      height: 0,
    },
  },
  mutations: {
    apiUrl(state, value) {
      state.apiUrl = value;
    },
    initialLocale(state, value) {
      state.initialLocale = value;
    },
    orderId(state, value) {
      state.orderId = value;
    },
    chargeId(state, value) {
      state.chargeId = value;
    },
    isACH(state, value) {
      state.isACH = value;
    },
    query(state, value) {
      state.query = value;
    },
    printReceiptData(state, value) {
      state.printReceiptData = value;
    },
    lastSize(state, value) {
      state.lastSize = value;
    },
    options(state, value) {
      state.options = value;
    },
  },
  actions: {
    async initState({ commit, dispatch }, { orderParams, options, query }) {
      commit('options', options);
      commit('apiUrl', options.apiUrl);
      commit('query', query);
      dispatch('setInitialLocale');

      if (options.layout === 'loading') {
        return;
      }

      const orderData = await dispatch('getPreparedOrderData', {
        orderParams,
        queryOrderId: getQueryOrderId(query),
        queryOrderToken: getQueryOrderToken(query),
      });
      if (orderData.lang) {
        dispatch('setInitialLocale', orderData.lang);
      }
      dispatch('PaymentForm/initState', { orderParams, orderData, options });
    },

    async getPreparedOrderData({ commit, dispatch }, { orderParams, queryOrderId, queryOrderToken }) {
      assert(
        orderParams || queryOrderId,
        'orderParams or queryOrderId is required to dispatch getPreparedOrderData',
      );
      let orderData;
      let orderId;
      let orderObj;
      let resolvedOrderToken;
      try {
        orderId = queryOrderId || orderParams.orderId;
        resolvedOrderToken = queryOrderToken || orderParams.orderToken;

        orderObj = { orderId, resolvedOrderToken };
        orderData = await dispatch('getOrderData', orderObj);
        commit('orderId', orderId);
      } catch (error) {
        let errorData = get(error, 'response.data');
        if (!errorData) {
          errorData = {
            code: 'fm000025',
            message: 'Unknown error. Try request later',
          };
        }
        // fm000023 - time to enter date on payment form expired
        if (errorData.code !== 'fm000023') {
          captureProductionException(error);
        }
        console.error(error);
        orderData = { error: errorData };
      }
      return orderData;
    },

    async getOrderData({ state }, orderObj) {
      const { data } = await axios.get(
        `${state.apiUrl}/orders/${orderObj.orderId}`,
        {
          headers: { Authorization: `Bearer ${orderObj.resolvedOrderToken}` },
        },
      );
      return data;
    },

    async getOrderPrintData({ state }) {
      state.printReceiptLoading = true;
      const charge_type = state.isACH ? 'ach_charge_id' : 'charge_id';
      await axios.get(
        `${state.apiUrl}/orders/custom/${state.orderId}?${charge_type}=${state.chargeId}`,
        {},
      ).then((response) => {
        state.printReceiptData = response.data.data;
        setTimeout(() => { state.printReceiptLoading = false; }, 2000);
      });
    },

    setInitialLocale({ commit }) {
      const initialLocale = 'en-US';
      commit('initialLocale', initialLocale);
    },

    async recreateOrder({ state, dispatch, rootState }, status) {
      dispatch('PaymentForm/setPaymentStatus', [status]);

      try {
        const { data } = await axios.post(
          `${rootState.apiUrl}/api/v1/order/recreate`,
          {
            order_id: state.orderId,
          },
        );
        if (state.query.order_id) {
          const query = {
            ...state.query,
            order_id: data.id,
          };
          const url = [window.location.pathname, qs.stringify(query)].filter(item => item).join('?');
          window.history.replaceState({}, '', url);
        }
        const orderData = await dispatch('getPreparedOrderData', { queryOrderId: data.id });
        dispatch('PaymentForm/initState', { orderData });
      } catch (error) {
        console.error(error);
        captureProductionException(error);
        dispatch('PaymentForm/setPaymentStatus', ['FAILED_TO_BEGIN', error]);
      }
    },

    reportResize({ state, commit }, newSize) {
      if (
        state.lastSize.width === newSize.width
        && state.lastSize.height === newSize.height
      ) {
        return;
      }
      commit('lastSize', newSize);
      postMessage('FORM_RESIZE', newSize);
    },
  },

  modules: {
    PaymentForm: PaymentFormStore,
  },
});
